import { useState } from "react";
import { Check, ChevronDown, PenLine } from "lucide-react";
import { Headline } from "./SiteShell";
import { StaffHomeScene } from "./StaffFilm";
import { Device } from "./kit";
import { aud } from "./tradeData";

// Small, static building blocks for the phone versions of the marketing site. No timers, no maps, no carousels.
export function MobileHero({ eyebrow, title, copy, paper = false, children, testid }) {
  return (
    <section className={paper ? "site-paper" : ""} data-testid={testid}>
      <div className="wrap pt-8 pb-12">
        <Headline paper={paper} eyebrow={eyebrow} size="lg" title={title} copy={copy} />
        {children}
      </div>
    </section>
  );
}

export function MobileSection({ index, eyebrow, title, copy, paper = false, children, testid, id }) {
  return (
    <section id={id} className={paper ? "site-paper" : "border-t border-white/10"} data-testid={testid}>
      <div className="wrap py-14">
        <Headline paper={paper} index={index} eyebrow={eyebrow} size="md" title={title} copy={copy} />
        {children}
      </div>
    </section>
  );
}

export function Accordion({ items, paper = false, testid = "acc", defaultOpen = null }) {
  const [open, setOpen] = useState(defaultOpen);
  const rule = paper ? "divide-zinc-900/10 border-zinc-900/10" : "divide-white/10 border-white/10";
  return (
    <div className={`divide-y border-y ${rule}`} data-testid={testid}>
      {items.map((it, i) => {
        const on = open === i;
        return (
          <div key={it.key || i}>
            <button type="button" onClick={() => setOpen(on ? null : i)} className="flex min-h-[56px] w-full items-center gap-3 py-3 text-left" aria-expanded={on} data-testid={`${testid}-${it.key || i}`}>
              {it.index && <span className="mono-label amber w-7 shrink-0">{it.index}</span>}
              {it.icon && <it.icon size={16} className={`shrink-0 ${paper ? "text-zinc-500" : "text-zinc-500"}`} />}
              <span className={`display min-w-0 flex-1 text-[16px] ${paper ? "text-zinc-950" : "text-white"}`}>{it.title}</span>
              {it.meta && <span className={`shrink-0 text-[12.5px] ${paper ? "text-zinc-500" : "text-zinc-500"}`}>{it.meta}</span>}
              <ChevronDown size={16} className={`shrink-0 transition-transform ${paper ? "text-zinc-400" : "text-zinc-600"} ${on ? "rotate-180" : ""}`} />
            </button>
            {on && <div className={`pb-4 text-[14px] leading-relaxed animate-in ${paper ? "text-zinc-600" : "text-zinc-400"} ${it.index ? "pl-10" : it.icon ? "pl-7" : ""}`}>{it.body}</div>}
          </div>
        );
      })}
    </div>
  );
}

export function Ticks({ items, paper = false, testid }) {
  return <ul className={`divide-y border-y text-[14px] ${paper ? "divide-zinc-900/10 border-zinc-900/10 text-zinc-700" : "divide-white/10 border-white/10 text-zinc-300"}`} data-testid={testid}>{items.map((g) => <li key={g} className="flex items-start gap-3 py-3"><span className={`mt-[3px] tick ${paper ? "bg-zinc-950 text-white" : "bg-amber-500 text-zinc-950"}`}><Check size={11} /></span>{g}</li>)}</ul>;
}

export function StaffPhoneStatic({ trade, time = "7:31" }) {
  return <div className="flex justify-center"><Device time={time} testid="mock-staff-phone-home" className="pointer-events-none"><div className="px-4 pb-5 pt-1"><StaffHomeScene clocked trade={trade} /></div></Device></div>;
}

export function PortalCard({ trade }) {
  return (
    <div className="rounded-2xl border border-zinc-900/10 bg-white p-4 text-zinc-950 shadow-[0_30px_60px_-40px_rgba(10,10,11,.45)]" data-testid="m-portal-card">
      <div className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-[.1em] text-amber-700"><span className="h-2 w-2 rounded-full bg-amber-500" /> Action required</div>
      <div className="mt-2 flex items-start justify-between gap-3"><div><div className="text-[15px] font-semibold">Variation V-104 · {trade.variation?.[0] || "Additional works"}</div><div className="text-[12px] text-zinc-500">{trade.customer} · {trade.job}</div></div><div className="text-right text-[16px] font-semibold">+{aud(trade.variation?.[1] || 1850)}</div></div>
      <div className="mt-3 grid grid-cols-[1fr_auto] gap-2"><span className="flex items-center justify-center gap-1.5 rounded-lg bg-zinc-950 py-2.5 text-[13px] font-semibold text-white"><PenLine size={13} /> Approve & sign</span><span className="flex items-center justify-center rounded-lg border border-zinc-300 px-3 py-2.5 text-[13px] font-medium text-zinc-700">Ask a question</span></div>
      <ol className="mt-4 space-y-1.5 border-t border-zinc-900/10 pt-3 text-[12.5px]">{trade.stages.slice(0, 5).map((s, i) => <li key={s} className="flex items-center gap-2"><span className={`flex h-4 w-4 items-center justify-center rounded-full ${i < 3 ? "bg-emerald-500 text-white" : i === 3 ? "border-2 border-amber-500" : "border border-zinc-300"}`}>{i < 3 && <Check size={10} strokeWidth={3} />}</span><span className={i > 3 ? "text-zinc-400" : i === 3 ? "font-semibold" : "text-zinc-700"}>{s}</span></li>)}</ol>
    </div>
  );
}

export function TradeChips({ value, onChange, trades, paper = true }) {
  return (
    <div className={`-mx-4 flex gap-1.5 overflow-x-auto px-4 pb-1 ${paper ? "" : ""}`} style={{ scrollbarWidth: "none" }} role="tablist" data-testid="trade-selector">
      {trades.map((tr) => <button key={tr.key} role="tab" aria-selected={tr.key === value} onClick={() => onChange(tr.key)} className={`shrink-0 whitespace-nowrap rounded-full px-3.5 py-2 text-[13px] font-semibold transition-colors ${tr.key === value ? "bg-amber-500 text-zinc-950" : paper ? "border border-zinc-900/15 text-zinc-700" : "border border-white/15 text-zinc-300"}`} data-testid={`trade-${tr.key}`}>{tr.label}</button>)}
    </div>
  );
}
